import type { AlarmAction, AlarmEventDto } from '@atnt/shared';
import { API_BASE } from '@/shared/api/client';

/**
 * Live alarm feed (`{ type: 'alarm', tenantId, action, event }`, packages/shared/src/schemas/live.ts).
 *
 * The constructor's value store only joins a tenant through indicator subscriptions, but the alarm feed is joined
 * by any `subscribe` — even with no ids — so the shell keeps this small socket of its own: one per browser tab,
 * ref-counted per tenant, reconnecting with backoff (1 → 30 s), pinging every 25 s. After a reconnect listeners get
 * `{ type: 'resync' }` to refetch what may have been missed.
 */

export type AlarmFeedMessage = { type: 'alarm'; tenantId: string; action: AlarmAction; event: AlarmEventDto } | { type: 'resync'; tenantId: string };
type Listener = (msg: AlarmFeedMessage) => void;

const BACKOFF_MIN = 1000;
const BACKOFF_MAX = 30_000;
const PING_EVERY_MS = 25_000;
const IDLE_CLOSE_MS = 10_000;

class AlarmFeed {
  #ws: WebSocket | null = null;
  #listeners = new Map<string, Set<Listener>>();
  #attempt = 0;
  #everOpened = false;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #pingTimer: ReturnType<typeof setInterval> | null = null;
  #idleTimer: ReturnType<typeof setTimeout> | null = null;

  /** Listen to the alarm events of a tenant (UUID); returns the unsubscribe function. */
  subscribe(tenantId: string, listener: Listener): () => void {
    let set = this.#listeners.get(tenantId);
    const fresh = !set;
    if (!set) this.#listeners.set(tenantId, (set = new Set()));
    set.add(listener);
    if (this.#idleTimer) {
      clearTimeout(this.#idleTimer);
      this.#idleTimer = null;
    }
    if (fresh) this.#send({ type: 'subscribe', tenantId, indicatorIds: [] });
    this.#ensure();
    return () => {
      const s = this.#listeners.get(tenantId);
      if (!s) return;
      s.delete(listener);
      if (s.size) return;
      this.#listeners.delete(tenantId);
      // leave the tenant's alarm feed (an empty id list), then close the socket when nothing is left
      this.#send({ type: 'unsubscribe', tenantId, indicatorIds: [] });
      if (!this.#listeners.size) this.#scheduleIdleClose();
    };
  }

  /** Development aid: feed a message as if it came from the server (`window.__atntAlarmFeed.inject(…)`). */
  inject(msg: AlarmFeedMessage) {
    this.#dispatch(msg);
  }

  #dispatch(msg: AlarmFeedMessage) {
    this.#listeners.get(msg.tenantId)?.forEach((l) => {
      try {
        l(msg);
      } catch (err) {
        if (import.meta.env.DEV) console.error('[alarm feed]', err);
      }
    });
  }

  #send(msg: object) {
    if (this.#ws?.readyState === WebSocket.OPEN) this.#ws.send(JSON.stringify(msg));
  }

  #ensure() {
    if (typeof window === 'undefined' || typeof WebSocket === 'undefined') return;
    if (this.#ws || this.#reconnectTimer || !this.#listeners.size) return;
    this.#connect();
  }

  #connect() {
    this.#reconnectTimer = null;
    if (!this.#listeners.size) return;
    const url = new URL(`${API_BASE}/live`, window.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      return this.#scheduleReconnect();
    }
    this.#ws = ws;
    ws.onopen = () => {
      if (this.#ws !== ws) return;
      const reconnected = this.#everOpened;
      this.#everOpened = true;
      this.#attempt = 0;
      for (const tenantId of this.#listeners.keys()) {
        this.#send({ type: 'subscribe', tenantId, indicatorIds: [] });
        if (reconnected) this.#dispatch({ type: 'resync', tenantId });
      }
      this.#pingTimer = setInterval(() => this.#send({ type: 'ping' }), PING_EVERY_MS);
    };
    ws.onmessage = (ev) => {
      if (this.#ws !== ws || typeof ev.data !== 'string') return;
      let msg: { type?: string; tenantId?: string } | null = null;
      try {
        msg = JSON.parse(ev.data) as { type?: string; tenantId?: string };
      } catch {
        return;
      }
      if (msg?.type === 'alarm' && typeof msg.tenantId === 'string') this.#dispatch(msg as AlarmFeedMessage);
    };
    ws.onclose = () => {
      if (this.#ws !== ws) return;
      this.#ws = null;
      if (this.#pingTimer) clearInterval(this.#pingTimer);
      this.#pingTimer = null;
      this.#scheduleReconnect();
    };
    ws.onerror = () => {
      /* followed by close */
    };
  }

  #scheduleReconnect() {
    if (this.#reconnectTimer || !this.#listeners.size) return;
    const base = Math.min(BACKOFF_MAX, BACKOFF_MIN * 2 ** this.#attempt);
    this.#attempt += 1;
    this.#reconnectTimer = setTimeout(() => this.#connect(), base * (0.7 + Math.random() * 0.6));
  }

  #scheduleIdleClose() {
    if (this.#idleTimer) return;
    this.#idleTimer = setTimeout(() => {
      this.#idleTimer = null;
      if (this.#listeners.size) return;
      if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
      if (this.#pingTimer) clearInterval(this.#pingTimer);
      this.#pingTimer = null;
      const ws = this.#ws;
      this.#ws = null;
      this.#everOpened = false;
      ws?.close();
    }, IDLE_CLOSE_MS);
  }
}

/** The tab-wide alarm feed. */
export const alarmFeed = new AlarmFeed();

if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __atntAlarmFeed?: AlarmFeed }).__atntAlarmFeed = alarmFeed;
}
